import type { SupporterRow, SupporterType } from '@/api/generated';

/**
 * The words the Supporters table shows, in one place, so the table and its
 * tests read the same module (P3, P6).
 *
 * A row carries only the keys of the generated `SupporterRow` (I1): the
 * backend's field registry decides what an organization may see, and a
 * person who has hidden themselves arrives with a null name and email. They
 * are still counted and listed, never named, and a withheld field reads as
 * "Not available" rather than an empty cell (backend D7).
 */

export { formatDate } from '@/lib/organization';

export const NOT_AVAILABLE = 'Not available';

/** For a column with nothing to say yet: Invitation Status on a followed row, Invitation Sent everywhere. */
export const DASH = '—';

const TYPE_WORDS: Record<SupporterType, string> = {
  FOLLOWED: 'Followed',
  INVITED: 'Invited',
};

export function typeWord(type: SupporterType): string {
  return TYPE_WORDS[type];
}

/** What the pending card and the disabled invite buttons say (P2, P7). */
export const ARRIVES_WITH_INVITATIONS = 'Arrives with member invitations';

/**
 * A live member row joined by accepting an invitation, so it reads
 * "Accepted"; a follower was never invited (I6). Any other member status -
 * pending arrives with member invitations (US-052) - has no word yet, so it
 * reads "—" rather than claiming an acceptance (P3).
 */
export function invitationStatusWord(row: SupporterRow): string {
  return row.type === 'INVITED' && row.status === 'ACTIVE' ? 'Accepted' : DASH;
}

export function notificationsWord(row: SupporterRow): string {
  return row.notifications_enabled ? 'Opted in' : 'Opted out';
}

export function shownName(row: SupporterRow): string {
  return row.name ?? NOT_AVAILABLE;
}

export function shownEmail(row: SupporterRow): string {
  return row.email ?? NOT_AVAILABLE;
}

/**
 * The avatar's letters: the first letters of the first and last word of the
 * name. Null when there is no name, so the avatar shows its neutral glyph
 * rather than letters from "Not available".
 */
export function initials(name: string | null): string | null {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}
